import React, { FC, useCallback, useMemo, useRef } from 'react';
import { useHistory } from 'react-router';

import DataGrid, {
  Column,
  ColumnChooser,
  Export,
  FilterPanel,
  FilterRow,
  GroupPanel,
  HeaderFilter,
  Pager,
  Paging,
  StateStoring,
  Summary,
  TotalItem,
} from 'devextreme-react/data-grid';

import { AppCard, AppLayoutTop } from 'components/appComponents';
import ClaimsProvider from 'components/claimsBlock';
import { MONEY_FORMAT } from 'constants/formats';
import { useAuth } from 'context/auth';
import { useModal } from 'context/ModalProvider/useModal';
import { Button, Template } from 'devextreme-react';
import $createStore from 'utils/create-store-instance';

import {
  CreateDriverSalaryAdjustmentsModal,
  DeleteDriverSalaryAdjustmentsModal,
  EditDriverSalaryAdjustmentsModal,
} from './modals';

const DriverSalaryAdjustments: FC = () => {
  const history = useHistory();
  const { getClaims }: any = useAuth();
  const { showModal } = useModal();

  const driverSalaryAdjustmentsStore: any = useMemo(() => {
    return $createStore('/v1/DriverPumpAdjustments/', 'id');
  }, []);

  const refGrid = useRef<DataGrid | null>(null);
  const refreshGrid = useCallback(() => refGrid.current?.instance.refresh(), []);

  const onRowDblClickHandler = ({ data }: any) => {
    if (getClaims(['CanCreatePumpAdjustments'])) {
      history.push(`salary-pump-adjustment/${data.id}`);
    }
  };

  const handleClickCreate = useCallback(() => {
    showModal(<CreateDriverSalaryAdjustmentsModal onRefresh={refreshGrid} type="pump" />);
  }, [showModal, refreshGrid]);

  const handleClickEdit = useCallback(
    (id: number | string) => {
      showModal(<EditDriverSalaryAdjustmentsModal id={Number(id)} onRefresh={refreshGrid} type="pump" />);
    },
    [showModal, refreshGrid],
  );

  const handleClickDelete = useCallback(
    (id: number | string) => {
      showModal(<DeleteDriverSalaryAdjustmentsModal id={Number(id)} onRefresh={refreshGrid} />);
    },
    [showModal, refreshGrid],
  );

  return (
    <div className="container-dev">
      <AppLayoutTop title="">
        <ClaimsProvider claimsBlock={['CanCreatePumpAdjustments']}>
          <Button width={120} text="Создать" type="default" onClick={handleClickCreate} />
        </ClaimsProvider>
      </AppLayoutTop>
      <AppCard>
        <DataGrid
          dataSource={driverSalaryAdjustmentsStore}
          showBorders={false}
          loadPanel={{
            enabled: true,
            text: 'Загрузка',
          }}
          key="id"
          ref={refGrid}
          allowColumnResizing
          columnResizingMode="nextColumn"
          columnMinWidth={50}
          allowColumnReordering
          onRowDblClick={onRowDblClickHandler}
          onToolbarPreparing={(e) => {
            e.toolbarOptions?.items?.unshift({
              location: 'after',
              template: 'refresh',
            });
          }}
        >
          <StateStoring enabled={true} type="localStorage" storageKey="driverSalaryAdjustments" />
          <FilterRow visible={true} />
          <FilterPanel visible={true} />
          <HeaderFilter visible />
          <GroupPanel visible={true} />
          <ColumnChooser enabled={true} />
          <Template name="refresh">
            <Button icon="refresh" onClick={refreshGrid} />
          </Template>

          <Column dataField="id" caption="Номер" width={80} />
          <Column dataField="date" dataType="date" caption="Дата" width={120} />
          <Column dataField="driverName" caption="Водитель" />
          <Column dataField="sum" caption="Сумма руб." format={MONEY_FORMAT} />
          <Column dataField="comment" caption="Комментарий" />
          <Column
            dataField=""
            cellRender={({ data }) => (
              <>
                <ClaimsProvider claimsBlock={['CanEditPumpAdjustments']}>
                  <Button icon="edit" type="default" stylingMode="text" onClick={() => handleClickEdit(data.id)} />
                </ClaimsProvider>
                <ClaimsProvider claimsBlock={['CanDeletePumpAdjustments']}>
                  <Button icon="trash" type="default" stylingMode="text" onClick={() => handleClickDelete(data.id)} />
                </ClaimsProvider>
              </>
            )}
          />

          <Export enabled={true} />
          <Paging defaultPageSize={15} />
          <Pager showPageSizeSelector={true} allowedPageSizes={[15, 30, 50, 100]} />
          <Summary>
            <TotalItem column="count" summaryType="sum" displayFormat="Итого: {0}" />
          </Summary>
        </DataGrid>
      </AppCard>
    </div>
  );
};

export default DriverSalaryAdjustments;
